/*
 * CHERRY Shopify theme — behaviour of the Pepper-style product page (sections/pepper-*.liquid, styles: pepper-pdp.css).
 * Port of the Next.js clone's public/pdp/pepper-pdp.js, driven by the JSON the sections print (product variants in
 * #pp-data, review blocks in [data-pp-reviews-data], bundle lines in #pp-kit-data). Several sections can each load this
 * file: it runs once and initialises every .pdp-p root (also after a section reload in the theme editor).
 *
 * Behaviour of the Pepper-style Cherry PDP (markup: src/components/pdp/*, styles: pepper-pdp.css).
 * Reproduces what wearpepper.com runs on its product page, with the values measured there
 * (reference/pepper/*.json):
 *  - gallery: Swiper (speed 300ms, ease, slidesPerView 1, loop, threshold 5px, longSwipesRatio .5, short swipes < 300ms)
 *  - carousels: Flickity physics (friction .28, selectedAttraction .025, wrapAround, groupCells, cellAlign left)
 *  - sticky add to cart: Pepper's IntersectionObserver rule on the add-to-cart form, `transition: all 300ms ease`,
 *    hidden with margin-bottom:-100% (mobile, slides up from the bottom) / margin-top:-100% (desktop, drops from the top)
 *  - accordions: height .2s, opacity .5s, margin .2s; chevron / plus-minus .4s
 * Cart: same requests and events as Impact's product form (theme.js), so the Cherry cart drawer, the header
 * count and the "Buy 1, Get 1 FREE" discount behave exactly as with the theme's own buttons.
 */
(() => {
  "use strict";
  if (window.__pepperPdp) return; // the script is included by every Pepper section
  window.__pepperPdp = true;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const DESKTOP = window.matchMedia("(min-width: 990px)");
  const mod = (n, m) => ((n % m) + m) % m;
  // Window listeners belong to the current product; a style/colour switch swaps the product without a reload
  // (see "soft product switch" below) and aborts them so the old page's handlers don't pile up.
  let life = new AbortController();
  const listen = (target, type, fn, opts) =>
    target.addEventListener(type, fn, { ...(typeof opts === "object" ? opts : { capture: !!opts }), signal: life.signal });

  /** Shopify money format of the shop (themeVariables.settings.moneyFormat, e.g. "£{{amount}}"); prices in cents. */
  function money(cents) {
    const fmt = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || "£{{amount}}";
    const n = Number(cents) || 0;
    const group = (v, dec, th, sep) => {
      const [i, d] = (v / 100).toFixed(dec).split(".");
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? sep + d : "");
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === "amount_no_decimals") return group(n, 0, ",", ".");
      if (k === "amount_with_comma_separator") return group(n, 2, ".", ",");
      if (k === "amount_no_decimals_with_comma_separator") return group(n, 0, ".", ",");
      if (k === "amount_with_apostrophe_separator") return group(n, 2, "'", ".");
      return group(n, 2, ",", ".");
    });
  }
  const readJson = (el) => {
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  };

  /* ------------------------------------------------------------------ cart (Impact product-form protocol) */
  async function addItems(items, busyEl) {
    if (busyEl) busyEl.setAttribute("aria-busy", "true");
    try {
      // same request as Impact's <product-form> (theme.js): the cart drawer / header count push their section ids,
      // the response carries them re-rendered and the drawer swaps them in on "cart:change"
      const sections = ["variant-added"];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const res = await fetch(ROOT + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items, sections: sections.join(","), sections_url: ROOT + "variants/" + items[0].id }),
      });
      const json = await res.json();
      if (!res.ok) {
        window.alert(json.description || json.message || "This item could not be added.");
        return false;
      }
      if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === "page") {
        window.location.href = ROOT + "cart";
        return true;
      }
      const cart = await (await fetch(ROOT + "cart.js")).json();
      cart.sections = json.sections;
      const target = busyEl || document.documentElement;
      target.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail: { items: json.items || [json], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
      return true;
    } finally {
      if (busyEl) busyEl.removeAttribute("aria-busy");
    }
  }

  /* ------------------------------------------------------------------ gallery (Swiper-like) */
  const SWIPER = { speed: 300, threshold: 5, longSwipesRatio: 0.5, shortSwipeMs: 300 };

  function Gallery(root) {
    const swiper = $(".pp-swiper", root);
    const track = $(".pp-track", root);
    let slides = $$(".pp-slide", track);
    let n = slides.length;
    let index = 0; // real index
    let pos = 1; // index in the track including the leading clone
    let width = swiper.clientWidth;
    let anim = false;
    const thumbs = $$("[data-pp-thumb]", root);

    function build() {
      $$(".pp-clone", track).forEach((c) => c.remove());
      slides = $$(".pp-slide", track);
      n = slides.length;
      if (n > 1) {
        const first = slides[0].cloneNode(true);
        const last = slides[n - 1].cloneNode(true);
        first.classList.add("pp-clone");
        last.classList.add("pp-clone");
        first.setAttribute("aria-hidden", "true");
        last.setAttribute("aria-hidden", "true");
        track.appendChild(first);
        track.insertBefore(last, slides[0]);
      }
      const dots = $(".pp-dots", root);
      if (dots && dots.children.length !== n) {
        dots.innerHTML = "";
        for (let i = 0; i < n; i++) {
          const b = document.createElement("button");
          b.type = "button";
          b.setAttribute("aria-label", "Go to slide " + (i + 1));
          dots.appendChild(b);
        }
      }
      if (dots) dots.style.display = n > 1 ? "" : "none";
    }

    function setX(x, ms) {
      track.style.transitionDuration = (ms || 0) + "ms";
      track.style.transform = "translate3d(" + x + "px,0,0)";
    }
    const base = () => (n > 1 ? -pos * width : 0);

    function updateUi() {
      $$(".pp-dots button", root).forEach((b, i) => b.classList.toggle("is-active", i === index));
      thumbs.forEach((t, i) => t.classList.toggle("is-active", i === index));
    }

    function slideTo(i, ms = SWIPER.speed) {
      if (n < 2) return;
      pos = i + 1;
      index = mod(i, n);
      anim = ms > 0;
      setX(base(), ms);
      updateUi();
      if (!ms) fix();
    }
    function fix() {
      anim = false;
      if (pos === 0 || pos === n + 1) {
        pos = index + 1;
        setX(base(), 0);
      }
    }
    track.addEventListener("transitionend", (e) => {
      if (e.target === track) fix();
    });

    // pointer swipe
    let start = null;
    let moved = false;
    swiper.addEventListener("pointerdown", (e) => {
      if (n < 2 || e.button > 0 || e.target.closest("button")) return;
      if (anim) fix();
      start = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId, locked: null };
      moved = false;
    });
    listen(window, 
      "pointermove",
      (e) => {
        if (!start || e.pointerId !== start.id) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (start.locked === null) {
          if (Math.abs(dx) < SWIPER.threshold && Math.abs(dy) < SWIPER.threshold) return;
          start.locked = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
          if (start.locked === "x") {
            try { swiper.setPointerCapture(e.pointerId); } catch { /* noop */ }
          }
        }
        if (start.locked !== "x") return;
        moved = true;
        // Swiper subtracts the threshold from the first move so the slide does not jump
        const d = dx - Math.sign(dx) * SWIPER.threshold;
        setX(base() + d, 0);
        e.preventDefault();
      },
      { passive: false },
    );
    const end = (e) => {
      if (!start || e.pointerId !== start.id) return;
      const s = start;
      start = null;
      if (s.locked !== "x") return;
      const dx = e.clientX - s.x;
      const dt = performance.now() - s.t;
      let target = index;
      if (dt < SWIPER.shortSwipeMs && Math.abs(dx) > SWIPER.threshold) target = index + (dx < 0 ? 1 : -1);
      else if (Math.abs(dx) > width * SWIPER.longSwipesRatio) target = index + (dx < 0 ? 1 : -1);
      slideTo(target);
    };
    listen(window, "pointerup", end);
    listen(window, "pointercancel", end);
    swiper.addEventListener("click", (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
    swiper.addEventListener("dragstart", (e) => e.preventDefault());

    root.addEventListener("click", (e) => {
      const dot = e.target.closest(".pp-dots button");
      if (dot) slideTo($$(".pp-dots button", root).indexOf(dot));
      const th = e.target.closest("[data-pp-thumb]");
      if (th) slideTo(Number(th.dataset.ppThumb));
      if (e.target.closest("[data-pp-gprev]")) slideTo(index - 1);
      if (e.target.closest("[data-pp-gnext]")) slideTo(index + 1);
      const fav = e.target.closest("[data-pp-fav]");
      if (fav) {
        const on = !fav.classList.contains("is-on");
        $$("[data-pp-fav]", root).forEach((f) => f.classList.toggle("is-on", on));
      }
    });

    const onResize = () => {
      width = swiper.clientWidth;
      setX(base(), 0);
    };
    new ResizeObserver(onResize).observe(swiper);

    build();
    setX(base(), 0);
    updateUi();

    return {
      slideTo(i) {
        const k = Number(i);
        if (Number.isInteger(k) && k >= 0 && k < n && k !== index) slideTo(k);
      },
      setImages(srcs, alt) {
        $$(".pp-slide", track).forEach((s) => s.remove());
        srcs.forEach((src) => {
          const d = document.createElement("div");
          d.className = "pp-slide";
          const img = document.createElement("img");
          img.src = src;
          img.alt = alt || "";
          img.draggable = false;
          d.appendChild(img);
          track.appendChild(d);
        });
        build();
        index = 0;
        pos = 1;
        setX(base(), 0);
        updateUi();
      },
    };
  }

  /* ------------------------------------------------------------------ carousel (Flickity physics) */
  const FLICKITY = { friction: 0.28, selectedAttraction: 0.025, dragThreshold: 3 };

  function Carousel(root) {
    const opts = Object.assign({ cellAlign: "left", wrapAround: true, groupCells: true }, JSON.parse(root.dataset.ppCarousel || "{}"));
    const vp = $(".pp-car-vp", root);
    const slider = $(".pp-car-slider", root);
    const dotsEl = $(".pp-car-dots", root);
    const prevBtn = $("[data-pp-prev]", root);
    const nextBtn = $("[data-pp-next]", root);
    let cells = [];
    let slides = [];
    let S = 0; // slideable width
    let W = 0;
    let wrap = false;
    let x = 0;
    let velocity = 0;
    let selected = 0;
    let targetX = 0;
    let animating = false;
    let resting = 0;
    let pointerDown = false;
    let dragX = 0;
    let dragStartX = 0;
    let dragStartPointer = null;
    let didDrag = false;
    let active = false;

    function measure() {
      const els = Array.from(slider.children).filter((c) => c.classList.contains("pp-cell"));
      W = vp.clientWidth;
      let acc = 0;
      cells = els.map((el) => {
        el.style.transform = "";
        const cs = getComputedStyle(el);
        const w = el.getBoundingClientRect().width + parseFloat(cs.marginRight || "0");
        const c = { el, x: acc, w };
        acc += w;
        return c;
      });
      S = acc;
      const maxW = Math.max(0, ...cells.map((c) => c.w));
      wrap = !!opts.wrapAround && S - maxW >= W - 1;
      // groupCells: true -> as many cells as fit in the viewport per slide
      slides = [];
      let cur = null;
      cells.forEach((c, i) => {
        if (!cur || (opts.groupCells && cur.w + c.w > W + 0.5) || !opts.groupCells) {
          cur = { target: c.x, w: 0, cells: [] };
          slides.push(cur);
        }
        cur.w += c.w;
        cur.cells.push(i);
      });
      if (!wrap && slides.length) {
        // contain like Flickity when not wrapping
        const maxTarget = Math.max(0, S - W);
        slides.forEach((s) => (s.target = Math.min(s.target, maxTarget)));
        slides = slides.filter((s, i) => i === 0 || s.target !== slides[i - 1].target);
      }
      const img = $(".pp-img", root);
      if (img) root.style.setProperty("--pp-img-h", img.getBoundingClientRect().height + "px");
      renderDots();
    }

    function renderDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = "";
      if (slides.length < 2) return;
      slides.forEach((_, i) => {
        const li = document.createElement("li");
        li.className = "dot";
        li.setAttribute("aria-label", "Page dot " + (i + 1));
        li.addEventListener("click", () => select(i, false));
        dotsEl.appendChild(li);
      });
      updateDots();
    }
    function updateDots() {
      if (dotsEl) Array.from(dotsEl.children).forEach((d, i) => d.classList.toggle("is-selected", i === selected));
    }

    function position() {
      // cells stay in the flex row; each one is translated from its natural position (c.x) to its slot.
      // Wrapping: each cell sits at its position modulo the slideable width, in [-cell width, S - cell width)
      cells.forEach((c) => {
        const left = wrap ? mod(c.x + x + c.w, S) - c.w : c.x + x;
        c.el.style.transform = "translateX(" + (left - c.x) + "px)";
      });
    }

    function animate() {
      if (pointerDown) {
        const dragVelocity = dragX - x;
        velocity += dragVelocity - velocity;
      } else {
        velocity += (targetX - x) * FLICKITY.selectedAttraction;
      }
      const prev = x;
      x += velocity;
      velocity *= 1 - FLICKITY.friction;
      position();
      if (!pointerDown && Math.round(x * 100) === Math.round(prev * 100)) resting++;
      else resting = 0;
      if (resting > 2) {
        animating = false;
        x = targetX;
        position();
        return;
      }
      requestAnimationFrame(animate);
    }
    function start() {
      resting = 0;
      if (!animating) {
        animating = true;
        requestAnimationFrame(animate);
      }
    }

    function slideX(i) {
      return -slides[i].target;
    }
    /** raw index: may be <0 or >=len for prev/next wrap; shortest: pick the nearest copy (dots). */
    function select(raw, isRaw = true) {
      if (!slides.length) return;
      const len = slides.length;
      if (!wrap) {
        selected = Math.max(0, Math.min(len - 1, raw));
        targetX = slideX(selected);
      } else {
        const i = mod(raw, len);
        let t = slideX(i);
        // align the target with the cycle the slider currently sits in
        const cycle = Math.round((x - t) / S);
        t += cycle * S;
        if (isRaw && raw !== i) {
          // wrapped by prev/next: keep moving in the same direction
          if (raw >= len && t > x) t -= S;
          if (raw < 0 && t < x) t += S;
        } else {
          const alt = [t - S, t, t + S];
          t = alt.reduce((a, b) => (Math.abs(b - x) < Math.abs(a - x) ? b : a));
        }
        selected = i;
        targetX = t;
      }
      updateDots();
      start();
    }

    function closestTo(rx) {
      let best = 0;
      let bestD = Infinity;
      let bestT = 0;
      slides.forEach((s, i) => {
        const t0 = -s.target;
        const k = wrap ? Math.round((rx - t0) / S) : 0;
        const cands = wrap ? [t0 + (k - 1) * S, t0 + k * S, t0 + (k + 1) * S] : [t0];
        cands.forEach((t) => {
          const d = Math.abs(t - rx);
          if (d < bestD) {
            bestD = d;
            best = i;
            bestT = t;
          }
        });
      });
      return { index: best, t: bestT };
    }

    function onDown(e) {
      if (!active || e.button > 0 || slides.length < 2) return;
      if (e.target.closest("select, button, input")) return;
      dragStartPointer = { x: e.clientX, y: e.clientY, id: e.pointerId, locked: null };
    }
    function onMove(e) {
      if (!dragStartPointer || e.pointerId !== dragStartPointer.id) return;
      const dx = e.clientX - dragStartPointer.x;
      const dy = e.clientY - dragStartPointer.y;
      if (dragStartPointer.locked === null) {
        if (Math.abs(dx) <= FLICKITY.dragThreshold && Math.abs(dy) <= FLICKITY.dragThreshold) return;
        dragStartPointer.locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (dragStartPointer.locked === "x") {
          pointerDown = true;
          didDrag = true;
          dragStartX = x;
          root.classList.add("is-dragging");
          try { vp.setPointerCapture(e.pointerId); } catch { /* noop */ }
          start();
        }
      }
      if (dragStartPointer.locked !== "x") return;
      let nx = dragStartX + dx;
      if (!wrap) {
        // rubber band past the ends (Flickity halves the overshoot)
        const min = slideX(slides.length - 1);
        if (nx > 0) nx *= 0.5;
        else if (nx < min) nx = (nx + min) * 0.5;
      }
      dragX = nx;
      e.preventDefault();
    }
    function onUp(e) {
      if (!dragStartPointer || e.pointerId !== dragStartPointer.id) return;
      const was = dragStartPointer.locked === "x";
      dragStartPointer = null;
      if (!was) return;
      pointerDown = false;
      root.classList.remove("is-dragging");
      // Flickity dragEndRestingSelect: resting position = x + velocity / (1 - frictionFactor)
      const restingX = x + velocity / FLICKITY.friction;
      const len = slides.length;
      const near = (i, ref) => {
        const t = slideX(mod(i, len));
        return wrap ? t + Math.round((ref - t) / S) * S : t;
      };
      let { index, t } = closestTo(restingX);
      if (index === selected) {
        // Flickity dragEndBoostSelect
        const distance = near(selected, x) - x;
        const delta = dragX - dragStartX;
        const boost = distance > 0 && delta > 0 ? 1 : distance < 0 && delta < 0 ? -1 : 0;
        if (boost && (wrap || (index + boost >= 0 && index + boost < len))) {
          index += boost;
          t = near(index, x);
          if (wrap && boost > 0 && t > x) t -= S;
          if (wrap && boost < 0 && t < x) t += S;
        }
      }
      selected = mod(index, len);
      targetX = wrap ? t : slideX(selected);
      updateDots();
      start();
    }

    vp.addEventListener("pointerdown", onDown);
    listen(window, "pointermove", onMove, { passive: false });
    listen(window, "pointerup", onUp);
    listen(window, "pointercancel", onUp);
    vp.addEventListener("click", (e) => {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
      didDrag = false;
    }, true);
    vp.addEventListener("dragstart", (e) => e.preventDefault());
    if (prevBtn) prevBtn.addEventListener("click", () => select(selected - 1));
    if (nextBtn) nextBtn.addEventListener("click", () => select(selected + 1));

    function enable() {
      const want = !(opts.mobileOnly && DESKTOP.matches);
      if (!want) {
        active = false;
        root.classList.remove("is-ready");
        cells.forEach((c) => (c.el.style.transform = ""));
        if (dotsEl) dotsEl.innerHTML = "";
        return;
      }
      active = true;
      root.classList.add("is-ready");
      measure();
      if (selected >= slides.length) selected = 0;
      x = targetX = slides.length ? slideX(selected) : 0;
      velocity = 0;
      position();
      if (prevBtn) prevBtn.hidden = slides.length < 2;
      if (nextBtn) nextBtn.hidden = slides.length < 2;
    }
    let rt;
    const relayout = () => {
      clearTimeout(rt);
      rt = setTimeout(enable, 100);
    };
    listen(window, "resize", relayout);
    $$("img", root).forEach((img) => !img.complete && img.addEventListener("load", relayout, { once: true }));
    enable();
    return { refresh: enable };
  }

  /* ------------------------------------------------------------------ accordions */
  function Accordions(root) {
    root.addEventListener("click", (e) => {
      const head = e.target.closest(".pp-acc-head, .pp-faq-head");
      if (!head || !root.contains(head)) return;
      const item = head.parentElement;
      const more = item.querySelector(":scope > .pp-acc-more");
      const open = !item.classList.contains("is-open");
      head.setAttribute("aria-expanded", String(open));
      if (open) {
        more.style.height = "0px";
        item.classList.add("is-open");
        const h = more.scrollHeight;
        void more.offsetHeight;
        more.style.height = h + "px";
        const done = (ev) => {
          if (ev.propertyName !== "height") return;
          more.removeEventListener("transitionend", done);
          if (item.classList.contains("is-open")) more.style.height = "auto";
        };
        more.addEventListener("transitionend", done);
      } else {
        more.style.height = more.scrollHeight + "px";
        void more.offsetHeight;
        item.classList.remove("is-open");
        more.style.height = "0px";
      }
    });
  }

  /* ------------------------------------------------------------------ variant picker (Pepper's variant-radios) */
  // Layout (top -> bottom): "Size: <band><cup>" summary row, Color swatches, Band Size pills, Cup Size pills, other
  // options. Works whatever the product's options are: separate Band / Cup options, one combined "Size" option
  // ("32AA": shown as Band + Cup pills named pp-band / pp-cup), a Color option (round swatches), or colours that are
  // sibling products (swatch links, see the soft switch below). Inputs of real options are named pp-opt<position>.
  const optionKind = (name) => {
    const n = String(name || "").toLowerCase();
    if (/colou?r/.test(n)) return "color";
    if (/band/.test(n)) return "band";
    if (/cup/.test(n)) return "cup";
    if (/size/.test(n)) return "size";
    return "other";
  };
  const splitSize = (value) => {
    const m = String(value || "").trim().match(/^(\d+)\s*([a-z]+)$/i);
    return m ? { band: m[1], cup: m[2].toUpperCase() } : null;
  };

  function VariantPicker(root, data, onChange) {
    const names = data.optionNames || (data.variants[0] ? data.variants[0].options.map((_, i) => "Option " + (i + 1)) : []);
    const kinds = names.map(optionKind);
    const sizeIdx = kinds.indexOf("size");
    const bandIdx = kinds.indexOf("band");
    const cupIdx = kinds.indexOf("cup");
    const virtual = sizeIdx >= 0 && bandIdx < 0 && cupIdx < 0 && data.variants.every((v) => splitSize(v.options[sizeIdx]));
    let variant = data.variants.find((v) => v.id === data.variant) || data.variants[0];

    const bandOf = (v) => (bandIdx >= 0 ? v.options[bandIdx] : virtual ? splitSize(v.options[sizeIdx]).band : undefined);
    const cupOf = (v) => (cupIdx >= 0 ? v.options[cupIdx] : virtual ? splitSize(v.options[sizeIdx]).cup : undefined);

    /** Option values asked by the inputs (`override`: one input's value applied on top of the current variant). */
    function wanted(override) {
      const values = variant.options.slice();
      let band = bandOf(variant);
      let cup = cupOf(variant);
      if (override) {
        if (override.name === "pp-band") band = override.value;
        else if (override.name === "pp-cup") cup = override.value;
        else values[Number(override.name.slice(6))] = override.value;
      }
      if (virtual) values[sizeIdx] = band + cup;
      return values;
    }
    const find = (values, availableOnly) =>
      data.variants.find((v) => (!availableOnly || v.available) && v.options.every((o, i) => o === values[i]));

    // Availability like Pepper's (Dawn) variant-radios: a value is offered when an available variant has it together
    // with the values chosen in the groups shown above it (Color -> Band -> Cup -> others), whatever comes below.
    const RANK = { color: 0, band: 1, size: 1, cup: 2, other: 3 };
    const facets = [];
    kinds.forEach((k, i) => {
      if (virtual && i === sizeIdx) {
        facets.push({ key: "pp-band", rank: 1, get: (v) => splitSize(v.options[i]).band });
        facets.push({ key: "pp-cup", rank: 2, get: (v) => splitSize(v.options[i]).cup });
      } else facets.push({ key: "pp-opt" + i, rank: RANK[k], get: (v) => v.options[i] });
    });
    function offered(name, value) {
      const f = facets.find((x) => x.key === name);
      if (!f) return true;
      const above = facets.filter((x) => x.rank < f.rank);
      return data.variants.some((v) => v.available && f.get(v) === value && above.every((x) => x.get(v) === x.get(variant)));
    }

    function sizeLabel(v) {
      const band = bandOf(v);
      const cup = cupOf(v);
      if (band !== undefined && cup !== undefined) return band + cup;
      return sizeIdx >= 0 ? v.options[sizeIdx] : "";
    }

    function render() {
      $$('input[name^="pp-opt"], input[name="pp-band"], input[name="pp-cup"]', root).forEach((input) => {
        if (input.closest("[data-pp-kit]")) return;
        const name = input.name;
        const current =
          name === "pp-band" ? bandOf(variant) : name === "pp-cup" ? cupOf(variant) : variant.options[Number(name.slice(6))];
        input.checked = input.value === current;
        const ok = offered(name, input.value);
        const box = input.parentElement;
        box.classList.toggle("is-soldout", !ok);
        box.classList.toggle("is-checked", input.checked);
      });
      $$("[data-pp-groupval]", root).forEach((el) => {
        const g = el.dataset.ppGroupval;
        el.textContent = g === "band" ? bandOf(variant) : g === "cup" ? cupOf(variant) : variant.options[Number(g)] || "";
      });
      $$("[data-pp-summary]", root).forEach((el) => (el.textContent = sizeLabel(variant)));
    }

    root.addEventListener("change", (e) => {
      const input = e.target.closest('input[name^="pp-opt"], input[name="pp-band"], input[name="pp-cup"]');
      if (!input || input.closest("[data-pp-kit]")) return;
      const values = wanted({ name: input.name, value: input.value });
      // exact combination, else the closest available variant that has the value just picked
      let next = find(values, false);
      if (!next) {
        const keep = (v) => {
          if (input.name === "pp-band") return bandOf(v) === input.value;
          if (input.name === "pp-cup") return cupOf(v) === input.value;
          return v.options[Number(input.name.slice(6))] === input.value;
        };
        const score = (v) => v.options.reduce((s, o, i) => s + (o === values[i] ? 1 : 0), 0) + (v.available ? 0.5 : 0);
        next = data.variants.filter(keep).sort((a, b) => score(b) - score(a))[0];
      }
      if (!next) return render();
      const colorChanged = next.media != null && next.media !== variant.media;
      variant = next;
      render();
      onChange(variant, colorChanged);
    });

    render();
    return {
      get variant() {
        return variant;
      },
      /** what a product switch keeps: the size (and every option except the colour) */
      keep() {
        const out = { band: bandOf(variant), cup: cupOf(variant), size: sizeLabel(variant), byName: {} };
        names.forEach((n, i) => kinds[i] !== "color" && (out.byName[n.toLowerCase()] = variant.options[i]));
        return out;
      },
    };
  }

  /** Variant of `data` (another product) with the size kept by VariantPicker.keep(), in stock. */
  function variantKeeping(data, keep) {
    if (!keep || !data || !data.variants) return null;
    const names = data.optionNames || [];
    return data.variants.find((v) => {
      if (!v.available) return false;
      return v.options.every((o, i) => {
        const kind = optionKind(names[i]);
        if (kind === "color") return true;
        if (kind === "band") return keep.band === undefined || o === keep.band;
        if (kind === "cup") return keep.cup === undefined || o === keep.cup;
        if (kind === "size") return !keep.size || o === keep.size;
        const k = keep.byName[String(names[i] || "").toLowerCase()];
        return k === undefined || o === k;
      });
    });
  }

  /** Card variant selects: show the chosen variant's image (option data-pp-img) */
  function showCardVariant(select) {
    const opt = select.options[select.selectedIndex];
    const img = opt && opt.dataset.ppImg && $(".pp-img img", select.closest("[data-pp-card]"));
    if (img) img.src = opt.dataset.ppImg;
  }
  /** "Complete Your Collection" follows the colour picked on the product (matching underwear, like Pepper) */
  function matchCardsToColor(root, color) {
    const key = String(color || "").toLowerCase();
    if (!key) return;
    $$(".pp-ggw [data-pp-card] select[data-pp-variant]", root).forEach((select) => {
      const opt = Array.from(select.options).find((o) => !o.disabled && o.textContent.split(" / ").some((part) => part.trim().toLowerCase() === key));
      if (opt && select.value !== opt.value) {
        select.value = opt.value;
        showCardVariant(select);
      }
    });
  }

  /* ------------------------------------------------------------------ product page */
  function initPdp(root) {
    const dataEl = $("#pp-data", root);
    if (!dataEl) return;
    const data = readJson(dataEl);
    if (!data) return;
    const labels = Object.assign({ addToCart: "Add to cart", soldOut: "Sold out", inStock: "In stock, ready for shipping", outOfStock: "Sold out" }, data.labels);
    const form = $("[data-pp-form]", root);
    const idInput = $("[data-pp-id]", root);
    const atc = $("[data-pp-atc]", root);
    let variant = data.variants.find((v) => v.id === data.variant) || data.variants[0];

    function render() {
      idInput.value = variant.id;
      atc.disabled = !variant.available;
      $("[data-pp-atc-label]", root).textContent = variant.available ? labels.addToCart : labels.soldOut;
      $("[data-pp-atc-price]", root).textContent = variant.available ? "- " + money(variant.price) : "";
      const stock = $("[data-pp-stock]", root);
      stock.classList.toggle("is-out", !variant.available);
      stock.querySelector("span").textContent = variant.available ? labels.inStock : labels.outOfStock;
      $$("[data-pp-price]", root).forEach((p) => {
        const sale = !p.closest(".pp-sticky") && variant.compareAt && variant.compareAt > variant.price;
        p.innerHTML = "<span" + (sale ? ' class="is-sale"' : "") + ">" + money(variant.price) + "</span>" + (sale ? "<s>" + money(variant.compareAt) + "</s>" : "");
      });
    }

    const picker = VariantPicker(root, data, (next, colorChanged) => {
      variant = next;
      render();
      const colorIdx = (data.optionNames || []).findIndex((n) => optionKind(n) === "color");
      if (colorIdx >= 0) matchCardsToColor(root, variant.options[colorIdx]);
      if (colorChanged) {
        const g = $("[data-pp-gallery]", root);
        if (g && g.__ppGallery) g.__ppGallery.slideTo(variant.media);
      }
      const url = new URL(location.href);
      url.searchParams.set("variant", String(variant.id));
      url.searchParams.delete("option_values");
      history.replaceState(history.state, "", url.toString());
      // same event as Impact's variant picker, for apps listening to it
      document.documentElement.dispatchEvent(new CustomEvent("variant:change", { bubbles: true, detail: { variant, previousVariant: null, productForm: form } }));
    });
    root.__ppPicker = picker;
    root.addEventListener("change", (e) => {
      const select = e.target.closest("[data-pp-card] select[data-pp-variant]");
      if (select) showCardVariant(select);
    });
    variant = picker.variant;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!variant.available) return;
      await addItems([{ id: variant.id, quantity: 1 }], atc);
    });

    // sticky add to cart (Pepper's StickyAddToCart.handleIntersection)
    const sticky = $("[data-pp-sticky]", root);
    if (sticky) initSticky(sticky, form, () => variant);

    rememberViewed(data.card);
    render();
  }

  function initSticky(sticky, form, getVariant) {
    const applySticky = (rect, intersecting) => {
      if (rect.y > 0) sticky.classList.add("is-hidden");
      else sticky.classList.toggle("is-hidden", intersecting);
    };
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => applySticky(entry.boundingClientRect, entry.isIntersecting === true));
    }).observe(form);
    // Same rule on scroll: IntersectionObserver can skip a crossing on programmatic jumps / throttled frames
    let stickyQueued = false;
    listen(window, 
      "scroll",
      () => {
        if (stickyQueued) return;
        stickyQueued = true;
        requestAnimationFrame(() => {
          stickyQueued = false;
          const rect = form.getBoundingClientRect();
          applySticky(rect, rect.bottom > 0 && rect.top < window.innerHeight);
        });
      },
      { passive: true },
    );
    $("[data-pp-sticky-atc]", sticky).addEventListener("click", async (e) => {
      e.preventDefault();
      const variant = getVariant();
      if (!variant.available) {
        form.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      await addItems([{ id: variant.id, quantity: 1 }], e.currentTarget);
    });
  }

  // quick adds (Complete Your Collection / Best Sellers), wherever the card is
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(".pdp-p [data-pp-quick]");
    if (!btn || btn.disabled) return;
    const card = btn.closest("[data-pp-card]");
    const id = Number($("[data-pp-variant]", card).value);
    if (id) await addItems([{ id, quantity: 1 }], btn);
  });

  /* ------------------------------------------------------------------ reviews */
  function starSvg(rating) {
    const P = "M12 1.6l3.1 6.6 7.1.9-5.2 5 1.3 7.1L12 17.8l-6.3 3.4L7 14.1 1.8 9.1l7.1-.9z";
    let out = '<span class="pp-stars" role="img" aria-label="' + rating + ' out of 5 stars">';
    for (let i = 1; i <= 5; i++) {
      const fill = rating >= i - 0.25 ? "currentColor" : rating >= i - 0.75 ? "url(#pp-half)" : "none";
      out += '<svg viewBox="0 0 24 23" aria-hidden="true"><path d="' + P + '" fill="' + fill + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
    }
    return out + "</span>";
  }
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

  function initReviews(root, data) {
    const box = $("[data-pp-reviews]", root);
    if (!box || !data || !data.reviews) return;
    const list = $("[data-pp-list]", box);
    const pager = $("[data-pp-pager]", box);
    const state = { sort: "relevant", rating: 0, q: "", page: 1 };

    function filtered() {
      let r = data.reviews.slice();
      if (state.rating) r = r.filter((x) => x.rating === state.rating);
      if (state.q) {
        const q = state.q.toLowerCase();
        r = r.filter((x) => (x.text + " " + x.name).toLowerCase().includes(q));
      }
      if (state.sort === "recent") r.sort((a, b) => b.time - a.time);
      else if (state.sort === "highest") r.sort((a, b) => b.rating - a.rating || b.time - a.time);
      else if (state.sort === "lowest") r.sort((a, b) => a.rating - b.rating || b.time - a.time);
      else if (state.sort === "photos") r = r.filter((x) => x.image);
      else r.sort((a, b) => (b.rating >= 4) - (a.rating >= 4) || b.time - a.time); // "Most Relevant"
      return r;
    }
    function item(r) {
      return (
        '<div class="pp-rev-item">' + starSvg(r.rating) +
        '<div class="pp-rev-who"><div class="pp-rev-name">' + esc(r.name) + (r.verified ? '<span class="pp-rev-verified">Verified</span>' : "") + "</div>" +
        '<div class="pp-rev-date">' + esc(r.date) + "</div></div>" +
        '<div class="pp-rev-body">' + esc(r.text) + "</div>" +
        (r.itemType ? '<div class="pp-rev-variant">Size: ' + esc(r.itemType) + "</div>" : "") +
        (r.image ? '<button type="button" class="pp-rev-img" data-pp-review-img="' + esc(r.id) + '"><img src="' + esc(r.image) + '" alt="Review photo by ' + esc(r.name) + '" loading="lazy"></button>' : "") +
        "</div>"
      );
    }
    function pages(total, cur) {
      if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 3) return [1, 2, 3, 4, "…", total];
      if (cur >= total - 2) return [1, "…", total - 3, total - 2, total - 1, total];
      return [1, "…", cur - 1, cur, cur + 1, "…", total];
    }
    function draw(scroll) {
      const all = filtered();
      const total = Math.max(1, Math.ceil(all.length / data.perPage));
      state.page = Math.min(state.page, total);
      const slice = all.slice((state.page - 1) * data.perPage, state.page * data.perPage);
      list.classList.add("is-loading");
      setTimeout(() => {
        list.innerHTML = slice.length ? slice.map(item).join("") : '<p class="pp-rev-empty">No reviews match your search.</p>';
        list.classList.remove("is-loading");
      }, 250);
      pager.innerHTML =
        '<button type="button" class="is-nav" data-page="' + (state.page - 1) + '"' + (state.page === 1 ? " disabled" : "") + ' aria-label="Previous page">‹</button>' +
        pages(total, state.page)
          .map((p) => (p === "…" ? "<span>...</span>" : '<button type="button" data-page="' + p + '"' + (p === state.page ? ' class="is-active"' : "") + ">" + p + "</button>"))
          .join("") +
        '<button type="button" class="is-nav" data-page="' + (state.page + 1) + '"' + (state.page === total ? " disabled" : "") + ' aria-label="Next page">›</button>';
      pager.style.display = all.length > data.perPage ? "" : "none";
      if (scroll) box.querySelector(".pp-rev-tools").scrollIntoView({ behavior: "smooth", block: "start" });
    }
    pager.addEventListener("click", (e) => {
      const b = e.target.closest("button[data-page]");
      if (!b || b.disabled) return;
      state.page = Number(b.dataset.page);
      draw(true);
    });
    $("[data-pp-sort]", box).addEventListener("change", (e) => {
      state.sort = e.target.value;
      state.page = 1;
      draw();
    });
    $("[data-pp-search]", box).addEventListener("input", (e) => {
      state.q = e.target.value.trim();
      state.page = 1;
      draw();
    });
    box.addEventListener("click", (e) => {
      const tool = e.target.closest("[data-pp-tool]");
      if (tool) {
        const panel = $('[data-pp-panel="' + tool.dataset.ppTool + '"]', box);
        const on = !panel.classList.contains("is-on");
        panel.classList.toggle("is-on", on);
        tool.classList.toggle("is-on", on);
        if (on && tool.dataset.ppTool === "search") $("input", panel).focus();
      }
      const rb = e.target.closest("[data-pp-rating]");
      if (rb) {
        const n = Number(rb.dataset.ppRating);
        state.rating = state.rating === n ? 0 : n;
        $$("[data-pp-rating]", box).forEach((b) => b.classList.toggle("is-on", Number(b.dataset.ppRating) === state.rating));
        state.page = 1;
        draw();
      }
      const img = e.target.closest("[data-pp-review-img]");
      if (img) {
        const r = data.reviews.find((x) => x.id === img.dataset.ppReviewImg);
        if (r) lightbox('<img src="' + esc(r.image) + '" alt=""><figcaption>' + starSvg(r.rating) + "<br><strong>" + esc(r.name) + "</strong> · " + esc(r.date) + "<br>" + esc(r.text) + "</figcaption>");
      }
      const strip = e.target.closest("[data-pp-strip]");
      if (strip) $(".pp-rev-strip", box).scrollBy({ left: Number(strip.dataset.ppStrip) * 324, behavior: "smooth" });
      const write = e.target.closest("[data-pp-write]");
      if (write) {
        if (write.dataset.ppWriteLink) window.location.href = write.dataset.ppWriteLink;
        else writeReview();
      }
    });
    const stripEl = $(".pp-rev-strip", box);
    if (stripEl) {
      const arrows = () => {
        $('[data-pp-strip="-1"]', box).hidden = stripEl.scrollLeft < 4;
        $('[data-pp-strip="1"]', box).hidden = stripEl.scrollLeft + stripEl.clientWidth >= stripEl.scrollWidth - 4;
      };
      stripEl.addEventListener("scroll", arrows, { passive: true });
      arrows();
    }
    draw();
  }

  function lightbox(inner) {
    const el = document.createElement("div");
    el.className = "pp-lightbox";
    el.innerHTML = '<figure>' + inner + '<button type="button" aria-label="Close">×</button></figure>';
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add("is-open"));
    const close = () => {
      el.classList.remove("is-open");
      setTimeout(() => el.remove(), 200);
      document.removeEventListener("keydown", onKey);
    };
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    el.addEventListener("click", (e) => {
      if (e.target === el || e.target.closest("button[aria-label=Close]")) close();
    });
    return el;
  }

  function writeReview() {
    const el = lightbox(
      '<form class="pp-write" style="display:grid;gap:10px">' +
        '<strong style="font-size:18px">Write a review</strong>' +
        '<input required name="name" placeholder="Your name" style="height:40px;padding:0 12px;border:1px solid #ccc;border-radius:5px;font:inherit">' +
        '<select name="rating" style="height:40px;padding:0 12px;border:1px solid #ccc;border-radius:5px;font:inherit"><option>5</option><option>4</option><option>3</option><option>2</option><option>1</option></select>' +
        '<textarea required name="body" rows="4" placeholder="Your review" style="padding:10px 12px;border:1px solid #ccc;border-radius:5px;font:inherit"></textarea>' +
        '<button type="submit" style="position:static;width:100%;height:40px;border-radius:4px;background:#231f20;color:#fff;font-weight:700;text-transform:uppercase;letter-spacing:1.4px;box-shadow:none">Submit review</button>' +
        "</form>",
    );
    $("form", el).addEventListener("submit", (e) => {
      e.preventDefault();
      e.target.innerHTML = "<strong>Thank you!</strong><p>Your review has been received and will appear once it is approved.</p>";
    });
  }

  /* ------------------------------------------------------------------ recently viewed */
  // Each product page stores its card ({handle, title, url, image, price}); the newest first, 12 max.
  const RECENT_KEY = "pp-recently-viewed";
  function readRecent() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      return Array.isArray(list) ? list.filter((p) => p && typeof p === "object" && p.handle) : [];
    } catch {
      return [];
    }
  }
  function rememberViewed(card) {
    if (!card || !card.handle) return;
    const list = [card].concat(readRecent().filter((p) => p.handle !== card.handle)).slice(0, 12);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch { /* storage unavailable */ }
  }
  function initRecent(root) {
    const sec = $("[data-pp-recent]", root);
    if (!sec) return;
    const limit = Number(sec.dataset.ppLimit) || 10;
    const items = readRecent().slice(0, limit);
    if (!items.length) return;
    const slider = $(".pp-car-slider", sec);
    slider.innerHTML = items
      .map((p) => {
        const url = p.url || ROOT + "products/" + p.handle;
        return (
          '<div class="pp-cell"><div class="pp-rv-card"><a class="pp-img" href="' + esc(url) + '" draggable="false"><img src="' + esc(p.image) +
          '" alt="' + esc(p.title) + '" loading="lazy" draggable="false"></a><a class="pp-ctitle" href="' + esc(url) + '">' + esc(p.title) +
          '</a><div class="pp-cprice">' + esc(p.price) + "</div></div></div>"
        );
      })
      .join("");
    sec.hidden = false;
    Carousel($(".pp-car", sec));
  }

  /* ------------------------------------------------------------------ bundle builder (Pepper starter-kit) */
  function initKit(root) {
    const kit = $("[data-pp-kit]", root);
    const dataEl = $("#pp-kit-data", root);
    if (!kit || !dataEl) return;
    const data = readJson(dataEl);
    if (!data || !data.lines) return;
    const sizeForm = $("[data-pp-kit-size]", kit);
    const colorForm = $("[data-pp-kit-color]", kit);
    const next = $("[data-pp-kit-next]", kit);
    const atc = $("[data-pp-kit-atc]", kit);
    const cards = $$("[data-pp-kit-card]", kit);
    const picks = cards.map(() => null);
    const placeholder = cards.length ? $("[data-pp-kit-colorname]", cards[0]).textContent : "Select colors";
    let size = null;
    const galleries = cards.map((c) => c.__ppGallery);
    // one fieldset per product option (Band Size, Cup Size, ...): the selected size is one value per fieldset
    const fieldsets = $$(".pp-kit-sizes fieldset", sizeForm);
    const selectedValues = () => fieldsets.map((f) => ($("input:checked", f) || {}).value);
    const matches = (v, values) => values.every((val, i) => val === undefined || v.options[i] === val);

    function validSize() {
      next.disabled = !selectedValues().every(Boolean);
    }
    function validColors() {
      atc.disabled = !picks.every((p) => p !== null);
    }
    function variantFor(color) {
      return color.variants.find((v) => size.every((val, i) => v.options[i] === val));
    }
    sizeForm.addEventListener("change", () => {
      // like StarterKit.onChangeHandler: values sold out with the other selected options are disabled
      const values = selectedValues();
      fieldsets.forEach((f, pos) => {
        $$("input", f).forEach((i) => {
          const want = values.map((val, j) => (j === pos ? i.value : val));
          const ok = data.lines.every((l) => l.colors.some((c) => c.variants.some((v) => v.available && matches(v, want))));
          i.disabled = !ok;
          i.parentElement.classList.toggle("is-soldout", !ok);
          i.parentElement.classList.toggle("is-checked", i.checked);
        });
      });
      validSize();
    });
    sizeForm.addEventListener("submit", (e) => {
      e.preventDefault();
      if (next.disabled) return;
      size = selectedValues();
      kit.dataset.selected = "true";
      cards.forEach((card, ci) => {
        const line = data.lines[ci];
        let any = false;
        $$("[data-pp-kit-swatch]", card).forEach((sw) => {
          const color = line.colors[Number(sw.dataset.ppKitSwatch)];
          const v = color && variantFor(color);
          const ok = !!(v && v.available);
          sw.classList.toggle("is-hidden", !ok);
          if (ok) any = true;
          if (!ok && picks[ci] === Number(sw.dataset.ppKitSwatch)) {
            picks[ci] = null;
            sw.classList.remove("is-active");
            $("[data-pp-kit-colorname]", card).textContent = placeholder;
            $("[data-pp-kit-check]", card).hidden = true;
          }
        });
        card.dataset.available = String(any);
      });
      validColors();
      colorForm.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    $("[data-pp-kit-edit]", kit).addEventListener("click", () => {
      kit.dataset.selected = "false";
    });
    colorForm.addEventListener("click", (e) => {
      const sw = e.target.closest("[data-pp-kit-swatch]");
      if (!sw) return;
      const card = sw.closest("[data-pp-kit-card]");
      const ci = Number(card.dataset.ppKitCard);
      const color = data.lines[ci].colors[Number(sw.dataset.ppKitSwatch)];
      picks[ci] = Number(sw.dataset.ppKitSwatch);
      $$("[data-pp-kit-swatch]", card).forEach((s) => s.classList.toggle("is-active", s === sw));
      $("[data-pp-kit-colorname]", card).textContent = color.name;
      $("[data-pp-kit-check]", card).hidden = false;
      if (galleries[ci]) galleries[ci].setImages(color.images, data.lines[ci].title + " - " + color.name);
      validColors();
    });
    colorForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (atc.disabled || !size) return;
      const variants = picks.map((p, ci) => variantFor(data.lines[ci].colors[p]));
      if (variants.some((v) => !v)) return;
      const ids = variants.map((v) => v.id).join(",");
      await addItems(
        variants.map((v, i) => ({ id: v.id, quantity: 1, properties: { _bundle_items_ids: ids, _line_item_index: String(i), _from_bundle_kit: "true" } })),
        atc,
      );
    });
  }

  /* ------------------------------------------------------------------ boot */
  function initRoot(root) {
    if (!root || root.__ppReady) return;
    root.__ppReady = true;
    $$("[data-pp-gallery]", root).forEach((g) => {
      const api = Gallery(g);
      g.__ppGallery = api;
      const card = g.closest("[data-pp-kit-card]");
      if (card) card.__ppGallery = api;
    });
    $$("[data-pp-carousel]", root).forEach((c) => {
      if (!c.closest("[data-pp-recent]")) Carousel(c);
    });
    $$("[data-pp-accordions]", root).forEach(Accordions);
    initPdp(root);
    initReviews(root, readJson($("[data-pp-reviews-data]", root)));
    initKit(root);
  }
  /* ------------------------------------------------------------------ soft product switch */
  // Each style (Lift Up / Multiway / Trio) and colour is its own product, so these links open another product
  // page. To make the switch instant: every sibling page is fetched as soon as the product page boots, parsed,
  // its first gallery images decoded and its <main> pre-imported; the click (mouse: pointerdown) then swaps the
  // page content in the same frame (<main>, so a different product template works too), keeps the scroll
  // position and the chosen band/cup, and updates the URL in place. If a page isn't ready yet, the clicked option
  // is marked active immediately and the content follows as soon as it arrives.
  const SWITCH_LINKS = ".pp-style-options a[href*='/products/'], .pp-swatches a[href*='/products/']";
  const pending = new Map(); // path -> Promise<Document>
  const ready = new Map(); // path -> Document (fully prepared)
  const pathOf = (href) => new URL(href, location.href).pathname;

  function decodeImages(doc) {
    return Promise.all(
      $$("#pp-main [data-pp-gallery] img", doc).slice(0, 3).map((img) => {
        const pre = new Image();
        pre.src = img.getAttribute("src");
        return pre.decode().catch(() => {});
      }),
    );
  }

  function fetchProduct(href) {
    const path = pathOf(href);
    if (!pending.has(path)) {
      const p = fetch(path, { credentials: "same-origin" })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then(async (html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          if (!$("main #pp-data", doc)) return Promise.reject("not a Pepper product page");
          await decodeImages(doc);
          doc.__ppFresh = document.importNode($("main", doc), true);
          ready.set(path, doc);
          return doc;
        });
      p.catch(() => pending.delete(path));
      pending.set(path, p);
    }
    return pending.get(path);
  }

  function markChosen(a) {
    const group = a.closest(".pp-style-options, .pp-swatches");
    $$("a", group).forEach((el) => {
      el.classList.toggle("is-active", el === a);
      if (el === a) el.setAttribute("aria-current", "true");
      else el.removeAttribute("aria-current");
    });
  }

  function swapTo(doc, href, keep) {
    const main = $("main");
    const nextMain = $("main", doc);
    if (!main || !nextMain) {
      location.href = href;
      return;
    }
    const fresh = doc.__ppFresh || document.importNode(nextMain, true);
    doc.__ppFresh = null;
    setTimeout(() => (doc.__ppFresh = document.importNode(nextMain, true)), 400); // ready for switching back
    const url = new URL(href, location.href);
    const dataEl = $("#pp-data", fresh);
    // keep the same size when the new product has it in stock
    const data = dataEl ? JSON.parse(dataEl.textContent) : null;
    const same = variantKeeping(data, keep);
    if (same) {
      data.variant = same.id;
      dataEl.textContent = JSON.stringify(data);
      url.searchParams.set("variant", String(same.id));
    }
    const scrollY = window.scrollY;
    life.abort();
    life = new AbortController();
    main.replaceChildren(...fresh.childNodes);
    document.title = doc.title;
    history.replaceState(history.state, "", url.pathname + url.search);
    window.scrollTo({ top: scrollY, behavior: "instant" });
    boot();
  }

  let switching = null;
  function switchProduct(a) {
    const href = a.href;
    const path = pathOf(href);
    if (switching === path || path === location.pathname) return; // already there (e.g. the click after a mouse press)
    switching = path;
    const pdp = $("#pp-main") && $("#pp-main").closest("[data-pp-root]");
    const keep = pdp && pdp.__ppPicker ? pdp.__ppPicker.keep() : null;
    const doc = ready.get(path);
    if (doc) {
      swapTo(doc, href, keep);
      switching = null;
      return;
    }
    markChosen(a);
    fetchProduct(href)
      .then((d) => switching === path && swapTo(d, href, keep))
      .catch(() => (location.href = href))
      .finally(() => switching === path && (switching = null));
  }

  const switchLink = (e) => {
    const a = e.target.closest && e.target.closest(SWITCH_LINKS);
    if (!a || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
    if (window.Shopify && window.Shopify.designMode) return null; // theme editor: keep normal links
    return a;
  };
  // mouse: react on press, not on release; touch/pen keep the click so a scroll starting on a swatch doesn't switch
  document.addEventListener("pointerdown", (e) => {
    const a = e.pointerType === "mouse" && switchLink(e);
    if (a && !a.classList.contains("is-active")) switchProduct(a);
  });
  document.addEventListener("click", (e) => {
    const a = switchLink(e);
    if (!a) return;
    e.preventDefault();
    if (!a.classList.contains("is-active")) switchProduct(a);
  });
  const warm = (e) => {
    const a = e.target.closest && e.target.closest(SWITCH_LINKS);
    if (a) fetchProduct(a.href).catch(() => {});
  };
  document.addEventListener("pointerover", warm, { passive: true });
  document.addEventListener("touchstart", warm, { passive: true });
  document.addEventListener("focusin", warm);

  function prefetchSwitchTargets() {
    $$(SWITCH_LINKS).forEach((a) => a.classList.contains("is-active") || fetchProduct(a.href).catch(() => {}));
  }

  function boot() {
    prefetchSwitchTargets();
    // product page first (it stores the "recently viewed" card), then every other Pepper section
    const roots = $$("[data-pp-root]");
    roots.filter((r) => $("#pp-data", r)).concat(roots).forEach(initRoot);
    roots.forEach(initRecent);
  }
  // theme editor: re-initialise a Pepper section when it is re-rendered
  document.addEventListener("shopify:section:load", (e) => {
    $$("[data-pp-root]", e.target).forEach((root) => {
      initRoot(root);
      initRecent(root);
    });
  });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
